import React, { useState, useCallback, useMemo, useEffect, forwardRef, memo } from "react";
import { TextInput, View, Platform } from "react-native";
import { PressableScale } from "pressto";
import Animated, { useAnimatedStyle, withTiming, Easing, useSharedValue } from "react-native-reanimated";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text } from "../Text";
import type { FormInputProps, InputSize } from "./types";
import { plainFormatter } from "./formatters";
import { Icon } from "../Icon";

const INPUT_HEIGHT: Record<InputSize, number> = {
  large: 48,
  medium: 44,
  small: 36,
};

const TEXT_SIZE: Record<InputSize, number> = {
  large: 16,
  medium: 16,
  small: 14,
};

/**
 * FormInput - Composable text input with pluggable formatters.
 *
 * Handles the common shell: animated container, focus/blur states,
 * error rendering, icon affordances, and spacing. Formatters handle
 * the specific format/parse logic for different input types.
 */
function FormInputComponent<TConfig = unknown>(
  {
    value,
    variant = "default",
    onChangeText,
    onChangeDisplayText,
    formatter = plainFormatter,
    formatterConfig,
    placeholder = "Enter text",
    title,
    error,
    size = "medium",
    iconLeft,
    iconRight,
    clearable,
    style,
    containerStyle,
    placeholderTextColor,
    textInputColor,
    textInputPaddingLeft,
    editable = true,
    keyboardType,
    returnKeyType = "done",
    autoCapitalize,
    onSubmitEditing,
    maxLength,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    ...props
  }: FormInputProps<TConfig>,
  ref: React.Ref<TextInput>,
) {
  const isFocused = useSharedValue(false);
  const { theme } = useUnistyles();
  const [isFocusedState, setIsFocusedState] = useState(false);

  // Initialize display value from formatted value
  const [displayValue, setDisplayValue] = useState(() =>
    value ? formatter.format(value, formatterConfig as TConfig) : "",
  );

  // Sync external value changes
  useEffect(() => {
    if (value !== undefined) {
      const formatted = formatter.format(value, formatterConfig as TConfig);
      setDisplayValue(formatted);
    }
  }, [value, formatter, formatterConfig]);

  const handleChangeText = useCallback(
    (text: string) => {
      const formatted = formatter.format(text, formatterConfig as TConfig);
      const parsed = formatter.parse(text, formatterConfig as TConfig);

      setDisplayValue(formatted);
      onChangeText(parsed);
      onChangeDisplayText?.(formatted);
    },
    [formatter, formatterConfig, onChangeText, onChangeDisplayText],
  );

  const textStyle = useMemo(() => (size === "small" ? "sm/regular" : "md/regular"), [size]);

  const selectionColor = useMemo(() => {
    if (variant === "outline") {
      return theme.colors.white.default;
    }
    return theme.colors.text;
  }, [variant, theme]);

  const containerBorderColor =
    containerStyle && typeof containerStyle === "object" && "borderColor" in containerStyle ?
      (containerStyle as { borderColor?: string }).borderColor
    : undefined;

  const borderColors = {
    outline: {
      focused: theme.colors.interactive.controls.focused_border_outline,
      unfocused: theme.colors.surface1.foregroundSecondary,
    },
    default: {
      focused: theme.colors.interactive.controls.focused_border,
      unfocused: theme.colors.border,
    },
  };

  const rContainerStyle = useAnimatedStyle(() => {
    if (error) {
      return {
        borderColor: theme.colors.status.error,
      };
    }

    if (containerBorderColor) {
      return {
        borderColor: containerBorderColor,
      };
    }

    const variantColors = borderColors[variant === "outline" ? "outline" : "default"];
    const borderColor = isFocused.value ? variantColors.focused : variantColors.unfocused;

    return {
      borderColor: withTiming(borderColor, { duration: 100, easing: Easing.out(Easing.ease) }),
    };
  });

  const placeholderColor = useMemo(() => {
    // Allow explicit override via prop
    if (placeholderTextColor) {
      return placeholderTextColor;
    }
    if (variant === "outline") {
      return theme.colors.white.default;
    }
    return theme.colors.textMuted;
  }, [variant, theme, placeholderTextColor]);

  const backgroundColor = useMemo(() => {
    if (variant === "outline") {
      return "transparent";
    }
    return theme.colors.surface;
  }, [variant, theme]);

  const textColor = useMemo(() => {
    if (variant === "outline") {
      return theme.colors.white.default;
    }
    return theme.colors.text;
  }, [variant, theme]);

  const inputPaddingLeft = useMemo(() => {
    // If there's a left icon, no extra padding needed (icon provides spacing)
    if (iconLeft) {
      return 0;
    }
    if (variant === "outline") {
      return theme.spacing["3xl"];
    }
    return theme.spacing.md;
  }, [variant, theme, iconLeft]);

  // Resolve keyboard type: prop > formatter default > "default"
  const resolvedKeyboardType = keyboardType ?? formatter.keyboardType ?? "default";
  // Resolve autoCapitalize: prop > formatter default > "none"
  const resolvedAutoCapitalize = autoCapitalize ?? formatter.autoCapitalize ?? "none";

  const showClearIcon = clearable && isFocusedState && !!displayValue;
  const rightIconNode =
    showClearIcon ?
      <PressableScale
        onPress={() => {
          handleChangeText("");
        }}
      >
        <Icon name="closeCircle" size={20} color={theme.colors.textMuted} />
      </PressableScale>
    : iconRight;

  const hasRightIcon = !!rightIconNode;

  return (
    <Animated.View style={style}>
      <Animated.View
        style={[styles.container, { height: INPUT_HEIGHT[size], backgroundColor }, containerStyle, rContainerStyle]}
      >
        {iconLeft && <View style={styles.iconLeft}>{iconLeft}</View>}
        {title ?
          <View style={styles.titleContainer}>
            <Text textStyle={textStyle} color="textMuted">
              {title}
            </Text>
          </View>
        : null}
        <TextInput
          ref={ref}
          style={[
            styles.input,
            {
              height: INPUT_HEIGHT[size],
              fontSize: TEXT_SIZE[size],
              color: textInputColor ?? textColor,
              paddingLeft: textInputPaddingLeft ?? inputPaddingLeft,
            },
            hasRightIcon ? { paddingRight: theme.spacing["10xl"] } : undefined,
            // Web-specific: remove default browser input border and outline
            Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : undefined,
          ]}
          value={displayValue}
          onChangeText={handleChangeText}
          placeholder={placeholder}
          placeholderTextColor={placeholderColor}
          keyboardType={resolvedKeyboardType}
          returnKeyType={returnKeyType}
          onSubmitEditing={onSubmitEditing}
          onFocus={(e) => {
            isFocused.value = true;
            setIsFocusedState(true);
            onFocusProp?.(e);
          }}
          onBlur={(e) => {
            isFocused.value = false;
            setIsFocusedState(false);
            onBlurProp?.(e);
          }}
          autoFocus={false}
          submitBehavior="newline"
          autoCorrect={false}
          autoCapitalize={resolvedAutoCapitalize}
          editable={editable}
          selectionColor={selectionColor}
          maxLength={maxLength}
          {...props}
        />
        {rightIconNode && <View style={styles.icon}>{rightIconNode}</View>}
      </Animated.View>
      {error && (
        <View style={styles.errorContainer}>
          <Icon name="errorOutline" size={18} color={theme.colors.danger[500]} />
          <Text textStyle="xs/medium" style={styles.error}>
            {error}
          </Text>
        </View>
      )}
    </Animated.View>
  );
}

export const FormInput = memo(forwardRef(FormInputComponent)) as <TConfig = unknown>(
  props: FormInputProps<TConfig> & { ref?: React.Ref<TextInput> },
) => React.ReactElement;

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing["2xl"],
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
  },
  titleContainer: {
    marginLeft: theme.spacing.lg,
  },
  iconLeft: {
    alignItems: "center",
    justifyContent: "center",
    width: theme.spacing["6xl"],
    height: theme.spacing["6xl"],
  },
  icon: {
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    right: theme.spacing["2xl"],
  },
  input: {
    flex: 1,
    minWidth: 0,
    color: theme.colors.text,
    paddingRight: theme.spacing.md,
    borderWidth: 0,
  },
  errorContainer: {
    marginTop: theme.spacing.xxs,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  error: {
    color: theme.colors.danger[500],
  },
}));

// Re-export types and formatters for convenience
export type { FormInputProps, InputFormatter, InputSize } from "./types";
export { plainFormatter, phoneFormatter, numericFormatter, otpFormatter } from "./formatters";
export type { PhoneFormatterConfig, OtpFormatterConfig } from "./formatters";
